import type { AnalyticsItem } from "@context/analytics-context";
import type { Order } from "@/types/order";
import {
  resolveCatalogContentId,
  resolveLineCatalogContentId,
} from "./catalog-content-id";

type MaybeNumber = number | string | null | undefined;

type ProductLike = {
  _id?: string;
  id?: string;
  slug?: string;
  name?: string;
  price?: MaybeNumber;
  originalPrice?: MaybeNumber;
  discountedPrice?: MaybeNumber;
  discount?: MaybeNumber;
  currency?: string;
  brand?: { name?: string } | null;
  categoryId?: { name?: string } | string | null;
  category?: { name?: string } | null;
};

type VariantLike = {
  catalogContentId?: string;
  _id?: string;
  id?: string;
  sku?: string;
  title?: string;
  name?: string;
  optionValues?: Record<string, string>;
};

type CartLike = {
  items?: Array<{
    productId?: string;
    productName?: string;
    productSlug?: string;
    price?: MaybeNumber;
    originalPrice?: MaybeNumber;
    currency?: string;
    quantity?: MaybeNumber;
    variantOptionValues?: Record<string, string> | null;
    variantId?: string;
    catalogContentId?: string;
  }>;
};

const asNumber = (value: MaybeNumber): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getCurrency = (
  product?: ProductLike | null,
  fallback?: string
): string | undefined => {
  if (!product) return fallback;
  if (product.currency && typeof product.currency === "string") {
    return product.currency.toUpperCase();
  }
  return fallback;
};

export function resolveProductPricing(
  product: ProductLike,
  variant?: VariantLike | null,
  fallbackCurrency?: string
): { unitPrice: number | undefined; currency?: string } {
  const candidateValues: MaybeNumber[] = [];
  if (variant && typeof (variant as any)?.price !== "undefined") {
    candidateValues.push((variant as any).price);
  }
  candidateValues.push(product.discountedPrice, product.price, product.originalPrice);
  const unitPrice = candidateValues
    .map(asNumber)
    .find((value): value is number => typeof value === "number" && Number.isFinite(value));
  const currency = getCurrency(product, fallbackCurrency);
  return { unitPrice, currency };
}

const buildItemId = (product: ProductLike, variant?: VariantLike | null): string => {
  return resolveCatalogContentId({
    productId: product._id || product.id || product.slug,
    variant,
  }) || "unknown";
};

const resolveCategory = (product: ProductLike): string | undefined => {
  if (typeof product.categoryId === "object" && product.categoryId && "name" in product.categoryId) {
    return (product.categoryId as { name?: string }).name || undefined;
  }
  if (typeof product.category === "object" && product.category && "name" in product.category) {
    return (product.category as { name?: string }).name || undefined;
  }
  if (typeof product.categoryId === "string") {
    return product.categoryId;
  }
  return undefined;
};

export function mapProductToItem(
  product: ProductLike,
  options?: { quantity?: number; variant?: VariantLike | null; index?: number; fallbackCurrency?: string }
): { item: AnalyticsItem; currency?: string; unitPrice?: number } {
  const quantity = typeof options?.quantity === "number" ? options.quantity : 1;
  const { unitPrice, currency } = resolveProductPricing(product, options?.variant, options?.fallbackCurrency);
  const item: AnalyticsItem = {
    item_id: buildItemId(product, options?.variant),
    item_name: product.name || "Unknown product",
    item_brand: product.brand?.name || undefined,
    item_category: resolveCategory(product),
    item_variant: options?.variant?.title || options?.variant?.name,
    price: unitPrice,
    quantity,
    index: typeof options?.index === "number" ? options.index : undefined,
  };
  return { item, currency, unitPrice };
}

export function mapCartToEvent(cart: CartLike): { items: AnalyticsItem[]; currency?: string; value?: number } {
  const items: AnalyticsItem[] = [];
  let currency: string | undefined;
  let totalValue = 0;
  for (const entry of cart.items || []) {
    const quantity = asNumber(entry.quantity) ?? 1;
    const price = asNumber(entry.price) ?? asNumber(entry.originalPrice) ?? 0;
    const itemCurrency = typeof entry.currency === "string" ? entry.currency.toUpperCase() : undefined;
    if (!currency && itemCurrency) {
      currency = itemCurrency;
    }
    items.push({
      item_id: resolveLineCatalogContentId(entry) || "unknown",
      item_name: entry.productName || "Cart item",
      price,
      quantity,
      item_variant: entry.variantOptionValues
        ? Object.values(entry.variantOptionValues).join(" / ")
        : undefined,
    });
    totalValue += price * quantity;
  }
  const value = Number.isFinite(totalValue) ? totalValue : undefined;
  return { items, currency, value };
}

export function mapOrderToEvent(order: Order): { items: AnalyticsItem[]; currency: string; value: number; coupon?: string | null } {
  const currency = order.currency || "USD";
  const items: AnalyticsItem[] = [];
  let total = 0;
  for (const line of order.items || []) {
    const quantity = asNumber(line.quantity) ?? 1;
    const price = asNumber(line.price) ?? 0;
    items.push({
      item_id: resolveLineCatalogContentId(line) || "unknown",
      item_name: line.productName || "Order item",
      price,
      quantity,
    });
    total += price * quantity;
  }
  const value = asNumber(order.total) ?? total;
  return { items, currency: currency.toUpperCase(), value: value ?? total, coupon: order.couponCode };
}
